import React from 'react';
import { Users, DollarSign, FileText, TrendingUp, Bell } from 'lucide-react';
import './SuperAdminDashboard.css';

import { SuperAdminContext } from './superadmin/SuperAdminContext';
import { useSuperAdminData } from './superadmin/useSuperAdminData';

import OverviewTab from './superadmin/tabs/OverviewTab';
import UsersTab from './superadmin/tabs/UsersTab';
import TransactionsTab from './superadmin/tabs/TransactionsTab';
import InquiriesTab from './superadmin/tabs/InquiriesTab';
import AutomationTab from './superadmin/tabs/AutomationTab';
import VerificationTab from './superadmin/tabs/VerificationTab';
import FeesTab from './superadmin/tabs/FeesTab';
import PayoutsTab from './superadmin/tabs/PayoutsTab';
import ReportsTab from './superadmin/tabs/ReportsTab';
import AgentsTab from './superadmin/tabs/AgentsTab';
import NotificationsTab from './superadmin/tabs/NotificationsTab';
import FinancialTab from './superadmin/tabs/FinancialTab';

const TAB_COMPONENTS = {
  overview: OverviewTab,
  users: UsersTab,
  transactions: TransactionsTab,
  inquiries: InquiriesTab,
  automation: AutomationTab,
  verification: VerificationTab,
  fees: FeesTab,
  payouts: PayoutsTab,
  reports: ReportsTab,
  agents: AgentsTab,
  notifications: NotificationsTab,
  financial: FinancialTab,
};

const TAB_BUTTONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'inquiries', label: 'Inquiries' },
  { key: 'automation', label: 'Automation' },
  { key: 'verification', label: 'Bank Verification' },
  { key: 'fees', label: 'Fee Management' },
  { key: 'payouts', label: 'Payouts' },
  { key: 'reports', label: 'Reports' },
  { key: 'agents', label: 'Agents' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'financial', label: 'Financial' },
];

const SuperAdminDashboard = () => {
  const ctx = useSuperAdminData();
  const {
    activeTab, setActiveTab, stats, loading, navigate, unreadCount,
  } = ctx;
  const ActiveTabComponent = TAB_COMPONENTS[activeTab];

  return (
    <SuperAdminContext.Provider value={ctx}>
      <div className="super-admin-dashboard" data-testid="super-admin-dashboard">
        <div className="dashboard-header">
          <h1>Super Admin Dashboard</h1>
          <button className="btn-secondary" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </button>
        </div>

        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#4ade80' }}><Users size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{stats.users.total}</div>
                <div className="stat-label">Total Users</div>
                <div className="stat-breakdown">
                  {stats.users.owners} Owners • {stats.users.agents} Agents • {stats.users.cashiers} Cashiers
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}><DollarSign size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">${stats.revenue.total_volume.toLocaleString()}</div>
                <div className="stat-label">Total Volume</div>
                <div className="stat-breakdown">
                  Est. Fees: ${stats.revenue.estimated_platform_fees.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#8b5cf6' }}><TrendingUp size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{stats.transactions.total}</div>
                <div className="stat-label">Transactions</div>
                <div className="stat-breakdown">
                  {stats.transactions.completed} Completed • {stats.transactions.pending} Pending
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#f59e0b' }}><FileText size={24} /></div>
              <div className="stat-content">
                <div className="stat-value">{stats.inquiries.total}</div>
                <div className="stat-label">Inquiries</div>
                <div className="stat-breakdown">{stats.inquiries.new} New</div>
              </div>
            </div>
          </div>
        )}

        <div className="tabs">
          {TAB_BUTTONS.map(({ key, label }) => (
            <button
              key={key}
              className={`tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
            >
              {key === 'notifications' && unreadCount > 0 && (
                <span className="notification-badge"><Bell size={14} /> {unreadCount}</span>
              )}
              {label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : ActiveTabComponent ? (
            <ActiveTabComponent />
          ) : null}
        </div>
      </div>
    </SuperAdminContext.Provider>
  );
};

export default SuperAdminDashboard;
